/**
 * Snap to FamBam: the camera key's sheet, and the only way to send a snap.
 *
 * It is the snap back sheet pointed at everyone. There used to be a page for
 * this with a rail of modes along the bottom, and once stories went there was
 * nothing on it but the rail. Now the key opens this over whatever you were
 * doing, and a tile opens the camera.
 *
 * A photo, a clip or something from the library is held up for a look before
 * it goes, the way the page did: the library has no retake step of its own,
 * and a snap reaches every phone. A GiviSnap hands its first shot to the
 * camera screen, which owns the selfie step and the post.
 */
import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CHAT_NAME, DAILY_NAME, SNAP_MAX_VIDEO_SECONDS, snapClipTooLong } from '../../../shared/constants';
import { lateMinutes } from '../../../shared/daily';
import { canRecordInApp } from '../media/record';
import { inspectVideo } from '../media/video';
import { go, useBackCloses } from '../router';
import { startGiviSnap } from '../screens/Camera';
import { useSwipeDown } from '../screens/Snap';
import type { SnapDecor } from '../../../shared/types';
import { lateText } from '../screens/Today';
import { queueSnap } from '../snapBack';
import { IconAperture, IconCamera, IconImage, IconVideo } from '../ui';
import { SnapCompose } from './SnapCompose';
import { VideoRecorder } from './VideoRecorder';

type Kind = 'photo' | 'video' | 'library';

interface Held {
  file: File;
  preview: string;
  video: boolean;
  from: Kind;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** A prompt is waiting to be answered, which is the only time a GiviSnap is offered. */
  promptOpen: boolean;
  /** When posting stops counting as on time, if the prompt has fired. */
  windowEndsAt: number | null;
}

/**
 * What the GiviSnap tile says under its name.
 *
 * On time it counts down; after that it says how late, in the words the feed
 * will badge the post with. The first minute past the window still counts as
 * on time -- lateness is stamped in whole minutes, rounded down -- so the
 * clock sits at nought for it rather than calling somebody late who will not
 * be marked late.
 */
export function promptLine(now: number, windowEndsAt: number | null): string | null {
  if (windowEndsAt === null) return null;
  const late = lateMinutes(new Date(now), new Date(windowEndsAt));
  if (late > 0) return `${lateText(late)} late`;
  const left = Math.max(0, Math.floor((windowEndsAt - now) / 1000));
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')} left to be on time`;
}

/** The time, once a second, while something on screen is counting. */
function useNow(ticking: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ticking]);
  return now;
}

export function SnapSheet({ open, onClose, promptOpen, windowEndsAt }: Props) {
  const [held, setHeld] = useState<Held | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The app's own camera, for a clip: 1080p, where the phone's was "medium".
  const [recordingClip, setRecordingClip] = useState(false);
  const now = useNow(open && promptOpen && windowEndsAt !== null);

  const backInput = useRef<HTMLInputElement>(null);
  const inputs: Record<Kind, RefObject<HTMLInputElement>> = {
    photo: useRef<HTMLInputElement>(null),
    video: useRef<HTMLInputElement>(null),
    library: useRef<HTMLInputElement>(null),
  };

  const close = () => {
    if (held) URL.revokeObjectURL(held.preview);
    setHeld(null);
    setError(null);
    onClose();
  };
  const swipe = useSwipeDown(close);
  useBackCloses(close, open);

  useEffect(() => {
    if (!open) return;
    const key = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });

  /** Checks what the camera or the library handed back, then holds it up. */
  const onPicked = async (file: File | undefined, from: Kind) => {
    if (!file) return;

    // Asked for a clip and handed a still: the camera opened in the wrong
    // mode. Send it anyway and the failure is invisible. Say what came back.
    if (from === 'video' && !file.type.startsWith('video/')) {
      setError('Your camera came back with a photo. Swipe it to video and try again.');
      return;
    }
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      setError('That is not a photo or a clip.');
      return;
    }

    const video = file.type.startsWith('video/');
    // Turned away here rather than failing in the queue. createUpload refuses
    // one too, and the transcoder cuts at the limit whatever it is told.
    if (video) {
      try {
        const facts = await inspectVideo(file);
        if (snapClipTooLong(facts.durationSec)) {
          setError(
            `That clip is ${Math.round(facts.durationSec)} seconds. A snap can be up to ${SNAP_MAX_VIDEO_SECONDS}.`,
          );
          return;
        }
      } catch {
        setError('That video could not be read.');
        return;
      }
    }

    setError(null);
    setHeld({ file, preview: URL.createObjectURL(file), video, from });
  };

  /** The first shot of a GiviSnap, handed to the screen that takes the selfie. */
  const onBackShot = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('That is not a photo. Pick a picture, or use the camera.');
      return;
    }
    startGiviSnap(file);
    close();
    go('camera');
  };

  const send = (decor: SnapDecor) => {
    if (!held) return;
    queueSnap(held.file, held.video, { decor });
    close();
    // Where it will appear, if the sheet was opened from somewhere else.
    go('chat');
  };

  /** Back to the same camera, from inside the tap so it is allowed to open. */
  const retake = () => {
    if (!held) return;
    const from = held.from;
    URL.revokeObjectURL(held.preview);
    setHeld(null);
    inputs[from].current?.click();
  };

  const discard = () => {
    if (!held) return;
    URL.revokeObjectURL(held.preview);
    setHeld(null);
  };

  if (!open) return null;

  const line = promptOpen ? promptLine(now, windowEndsAt) : null;
  // With a GiviSnap waiting, it is the one to reach for and the rest step back.
  const quiet = promptOpen ? ' snap-choose-kind--quiet' : '';

  return createPortal(
    <div
      className="snap-choose-scrim"
      onClick={() => !swipe.wasPull() && close()}
      {...swipe.area}
    >
      <div
        className="snap-choose"
        role="dialog"
        aria-label={`Snap to ${CHAT_NAME}`}
        onClick={(event) => event.stopPropagation()}
        style={swipe.style}
      >
        {/*
          Mounted with the sheet and never moved: the camera opens over the
          page, and when it hands back its picture these are what hear it.
        */}
        <input
          ref={backInput}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => onBackShot(e.target.files?.[0])}
        />
        <input
          ref={inputs.photo}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => void onPicked(e.target.files?.[0], 'photo')}
        />
        <input
          ref={inputs.video}
          type="file"
          accept="video/*"
          capture
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => void onPicked(e.target.files?.[0], 'video')}
        />
        <input
          ref={inputs.library}
          type="file"
          accept="image/*,video/*"
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => void onPicked(e.target.files?.[0], 'library')}
        />

        <span className="snap-sheet-grip" aria-hidden="true" />
        <span className="snap-choose-title">Snap to {CHAT_NAME}</span>

        {/* Full screen over the sheet, which waits underneath with its choices. */}
        {held && (
          <SnapCompose
            key={held.preview}
            shot={held}
            to={CHAT_NAME}
            onRetake={retake}
            onDiscard={discard}
            onSend={send}
          />
        )}
        {!promptOpen && (
          <span className="tiny dim snap-choose-note">Everyone gets one look.</span>
        )}
        {promptOpen && (
          <button
            type="button"
            className="snap-choose-kind snap-choose-kind--wide"
            onClick={() => backInput.current?.click()}
          >
            <IconAperture size={36} />
            <span className="snap-choose-kind-text">
              {DAILY_NAME}
              {line && <span className="snap-choose-when">{line}</span>}
            </span>
          </button>
        )}
        <div className="snap-choose-kinds">
          <button
            type="button"
            className={`snap-choose-kind${quiet}`}
            onClick={() => inputs.photo.current?.click()}
          >
            <IconCamera size={30} />
            Photo
          </button>
          <button
            type="button"
            className={`snap-choose-kind${quiet}`}
            onClick={() => (canRecordInApp() ? setRecordingClip(true) : inputs.video.current?.click())}
          >
            <IconVideo size={30} />
            Video
          </button>
        </div>
        <button
          type="button"
          className="snap-choose-kind snap-choose-kind--quiet snap-choose-kind--row"
          onClick={() => inputs.library.current?.click()}
        >
          <IconImage size={22} />
          From your photos
        </button>

        {error && <span className="snap-choose-error">{error}</span>}

        {recordingClip && (
          <VideoRecorder
            onRecorded={(file) => {
              setRecordingClip(false);
              void onPicked(file, 'video');
            }}
            onCancel={() => setRecordingClip(false)}
            onUsePhoneCamera={() => {
              setRecordingClip(false);
              inputs.video.current?.click();
            }}
          />
        )}

        <button type="button" className="snap-sheet-close" onClick={close}>
          Cancel
        </button>
      </div>
    </div>,
    document.body,
  );
}
